import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../hooks/redux';
import { RootState } from '../../store';
import { createPlant, getPlantById, deletePlantById, getDistrictCustomerSiteInfo, DistrictCustomerSiteInfo, getPlantsList, CreatePlantEditPayload } from '../../Services/serviceURL';
import { editPlantThunk } from './CreatePlantSlice';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import MapSelector from '../../Components/common/MapSelector';
import Loader from '../../Components/common/loader/loader';
import ConfirmationModal from '../../Components/common/ConfirmationModal';

import './createPlant.css';
import  apiInstance  from '../../config/HttpCommon';
import { log } from 'console';

export interface CustomerLite {
  id: string;
  customerId: string;
  customerName: string;
  soldTo?: string;
  sites?: Site[]; // Add sites array to customer interface
}

// Add correct Site and Asset interfaces
interface Site {
  siteId: string;
  site: string;
  latitude: string;
  longitude: string;
}

interface PlantInfo {
  customer: string;
  site: string;
  plantName: string;
  status: 'Active' | 'Inactive';
  latitude: string;
  longitude: string;
}

interface RawPlantApiResponse {
  id: string;
  customerId: string;
  siteId: string;
  site: string;
  siteStatus: string;
  country: string | null;
  salesDistrict: string | null;
  salesRegion: string | null;
  state: string | null;
  city: string | null;
  latitude: string | null;
  longitude: string | null;
}

interface Parameter {
  tag: string;
  sensorName: string;
  displayName: string;
}

interface SubAsset {
  id: string;
  type: string;
  name: string;
  status: string;
  parameters: Parameter[];
}

interface Asset {
  id: string;
  type: string;
  name: string;
  status: string;
  isDeleted: boolean;
  parameters: Parameter[];
  subAssets: SubAsset[];
}

interface PlantApiResponse {
  id: string;
  customer: string;
  site: string;
  plant: string;
  status: string;
  assets: Asset[];
}

type GUID = string;

const CreatePlant: React.FC = () => {
  const { plantId } = useParams<{ plantId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [currentStep] = useState(1);
  // Step 1: Add mode state
  const [mode, setMode] = useState<'create' | 'edit'>(plantId ? 'edit' : 'create');
  const [customers, setCustomers] = useState<CustomerLite[]>([]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedCustomer, setSelectedCustomer] = useState<CustomerLite | null>(null);
  const [plantInfo, setPlantInfo] = useState<PlantInfo>({
    customer: '',
    site: '',
    plantName: '',
    status: 'Active',
    latitude: '',
    longitude: ''
  });
  const [deleting, setDeleting] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [plantData, setPlantData] = useState<any>(null);
  const [selectedPlantId, setSelectedPlantId] = useState<GUID | null>(null);
  // Add state to track original plant data for change detection
  const [originalPlantInfo, setOriginalPlantInfo] = useState<PlantInfo | null>(null);
  
  // Searchable dropdown states
  const [isCustomerDropdownOpen, setIsCustomerDropdownOpen] = useState(false);
  const [customerSearchText, setCustomerSearchText] = useState('');
  const [isSiteDropdownOpen, setIsSiteDropdownOpen] = useState(false);
  const [siteSearchText, setSiteSearchText] = useState('');
  const [sites, setSites] = useState<Site[]>([]);
  const [coordinatesDisplay, setCoordinatesDisplay] = useState<string>('');

  const dispatch = useAppDispatch();
  const plantInfoRedux = useAppSelector((state: RootState) => state.plantInfo.plantInfo);
  const [assets, setAssets] = useState<Asset[]>([]); // <-- Add this line
  // Track previous plantId to detect changes
  const [prevPlantId, setPrevPlantId] = useState<string | undefined>(plantId);

  // Filtered lists for searchable dropdowns
  const filteredCustomers = useMemo(() => {
    const list = customers || [];
    const normalize = (s: any) => String(s ?? '').toLowerCase().replace(/\s+/g, ' ').trim();
    const term = normalize(customerSearchText);
    if (!term) return list;
    return list.filter(c => {
      const name = normalize(c.customerName);
      const soldTo = normalize(c.soldTo);
      if (name === term || soldTo === term) return true;
      return name.includes(term) || soldTo.includes(term);
    });
  }, [customers, customerSearchText]);

  const filteredSites = useMemo(() => {
    const list = sites || [];
    const normalize = (s: any) => String(s ?? '').toLowerCase().replace(/\s+/g, ' ').trim();
    const term = normalize(siteSearchText);
    if (!term) return list;
    return list.filter(s => {
      const siteName = normalize(s.site);
      const siteId = normalize(s.siteId);
      if (siteName === term || siteId === term) return true;
      return siteName.includes(term) || siteId.includes(term);
    });
  }, [sites, siteSearchText]);

  // Sync coordinates display with latitude and longitude values
  useEffect(() => {
    if (plantInfo.latitude && plantInfo.longitude) {
      const displayValue = `${plantInfo.latitude}, ${plantInfo.longitude}`;
      setCoordinatesDisplay(displayValue);
    } else {
      setCoordinatesDisplay('');
    }
  }, [plantInfo.latitude, plantInfo.longitude]);

  // Close any open dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Element | null;
      if (!target) return;
      const inDropdown = target.closest('.searchable-select-container');
      if (!inDropdown) {
        setIsCustomerDropdownOpen(false);
        setIsSiteDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Effect to handle plant ID changes and cleanup
  useEffect(() => {
    // Check if plantId has actually changed
    if (prevPlantId !== plantId) {
      // Clear previous plant data when switching plants
      if (prevPlantId && plantId && prevPlantId !== plantId) {
        // Switching from one plant to another - clear everything
        localStorage.removeItem('createPlantFormData');
        localStorage.removeItem('currentPlantData');
        localStorage.removeItem('currentPlantId');
        localStorage.removeItem('currentplantguid');
        localStorage.removeItem('customerid');
        
        // Reset all state
        setPlantInfo({
          customer: '',
          site: '',
          plantName: '',
          status: 'Active',
          latitude: '',
          longitude: ''
        });
        setSelectedCustomer(null);
        setOriginalPlantInfo(null);
        setPlantData(null);
        setSites([]);
        setCoordinatesDisplay('');
        
        dispatch({ type: 'createPlant/resetPlantForm' });
      }
      
      setPrevPlantId(plantId);
    }

    // Update mode if plantId changes
    setMode(plantId ? 'edit' : 'create');
    
    // Clear localStorage and reset state when switching to create mode
    if (!plantId) {
      // Clear all plant-related localStorage data
      localStorage.removeItem('createPlantFormData');
      localStorage.removeItem('currentPlantData');
      localStorage.removeItem('currentPlantId');
      localStorage.removeItem('currentplantguid');
      localStorage.removeItem('customerid');
      
      // Reset form state when switching to create mode
      setPlantInfo({
        customer: '',
        site: '',
        plantName: '',
        status: 'Active',
        latitude: '',
        longitude: ''
      });
      setSelectedCustomer(null);
      setOriginalPlantInfo(null);
      setPlantData(null);
      setSites([]);
      setCoordinatesDisplay('');
      
      // Reset Redux state
      dispatch({ type: 'createPlant/resetPlantForm' });
    }
  }, [plantId, prevPlantId, dispatch]);

  useEffect(() => {
    const fetchInitialData = async () => {
      setLoading(true);
      setError(null);
      try {
        // Fetch customers using new API endpoint
        const districtCustomerData = await getDistrictCustomerSiteInfo();
        
        // Ensure we have an array to work with
        if (!Array.isArray(districtCustomerData)) {
          setCustomers([]);
          return;
        }
        
        // Transform the API response to match CustomerLite interface
        const validCustomers = districtCustomerData.map((item: DistrictCustomerSiteInfo, index: number) => {
          // Transform sites from the API response
          const transformedSites: Site[] = item.sites?.map(site => ({
            siteId: site.siteId.toString(),
            site: site.name,
            latitude: '0', // Default values as they're not provided by the API
            longitude: '0'
          })) || [];

          return {
            id: `customer-${index}`, // Generate a unique ID
            customerId: item.salesDistrictCustomer,
            customerName: item.salesDistrictCustomer,
            soldTo: '', // Not provided in the new API response
            sites: transformedSites
          };
        });
        
        setCustomers(validCustomers);

        // If we have a plantId, fetch the plant data
        if (plantId) {
          const guidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
          let guidToUse = '';
          
          // Strategy 1: Check if URL plantId is already a GUID
          if (guidRegex.test(plantId)) {
            guidToUse = plantId;
          } else {
            // Strategy 2: Try to get GUID from localStorage
            const plantGuidFromStorage = localStorage.getItem('currentplantguid') || '';
            if (plantGuidFromStorage && guidRegex.test(plantGuidFromStorage)) {
              guidToUse = plantGuidFromStorage;
            } else {
              // Strategy 3: Check stored plant data for GUID
              try {
                const storedPlantData = localStorage.getItem('currentPlantData');
                if (storedPlantData) {
                  const parsedData = JSON.parse(storedPlantData);
                  if (parsedData.plantId && guidRegex.test(parsedData.plantId)) {
                    guidToUse = parsedData.plantId;
                  }
                }
              } catch (parseError) {
                // Error parsing stored data
              }
            }
          }

          // If we have a valid GUID, try API call
          if (guidToUse && guidRegex.test(guidToUse)) {
            try {
              const plantResponse = await getPlantById(guidToUse);      
              if (plantResponse.data && plantResponse.data.Success && plantResponse.data.Data) {
                localStorage.setItem("currentplantguid",plantResponse.data.Data.plantId)
                localStorage.setItem("currentPlantId",plantResponse.data.Data.id) // Store ObjectId for asset API
                localStorage.setItem("customerid",plantResponse.data.Data.customerId)
                const plant = plantResponse.data.Data;
                setPlantData(plant);
                // SET ASSETS HERE - ADD THIS CODE
                if (plant.assets && Array.isArray(plant.assets)) {
                setAssets(plant.assets); // <-- This is the missing line
               } else {
                setAssets([]);
                }
      
                // Set selectedPlantId to the plantId from the API response
                setSelectedPlantId(plant.plantId || plant.id || null);
                // Set the plant info first
                const updatedPlantInfo = {
                  customer: plant.customer,
                  site: plant.site,
                  plantName: plant.plant,
                  status: plant.status as 'Active' | 'Inactive',
                  latitude: plant.latitude || '',
                  longitude: plant.longitude || ''
                };
                
                setPlantInfo(updatedPlantInfo);
                // Store original plant info for change detection
                setOriginalPlantInfo({ ...updatedPlantInfo });
                
                // Find and set the selected customer using the local validCustomers array
                // Try multiple matching strategies for better compatibility
                let customer = validCustomers.find((c: CustomerLite) => 
                  c.customerId === plant.customer || c.customerName === plant.customer
                );
                
                // If exact match fails, try normalized search
                if (!customer) {
                  const normalizeString = (str: string) => str.toLowerCase().trim().replace(/\s+/g, ' ');
                  const normalizedPlantCustomer = normalizeString(plant.customer);
                  
                  customer = validCustomers.find((c: CustomerLite) => 
                    normalizeString(c.customerId) === normalizedPlantCustomer || 
                    normalizeString(c.customerName) === normalizedPlantCustomer
                  );
                }
                
                // If still not found, try partial match
                if (!customer) {
                  customer = validCustomers.find((c: CustomerLite) => 
                    c.customerName.includes(plant.customer) || plant.customer.includes(c.customerName)
                  );
                }
                
                if (customer) {
                  setSelectedCustomer(customer);
                  // Set sites directly from customer data instead of fetching separately
                  if (customer.sites && customer.sites.length > 0) {
                    setSites(customer.sites);
                  }
                }
              } else {
                throw new Error('Invalid API response');
              }
            } catch (apiError) {
              // Fall back to localStorage if available
              const storedPlantData = localStorage.getItem('currentPlantData');
              if (storedPlantData) {
                try {
                  const plant = JSON.parse(storedPlantData);
                  setPlantData(plant);
                  const updatedPlantInfo = {
                    customer: plant.customer,
                    site: plant.site,
                    plantName: plant.plant,
                    status: plant.status as 'Active' | 'Inactive',
                    latitude: plant.latitude || '',
                    longitude: plant.longitude || ''
                  };
                  setPlantInfo(updatedPlantInfo);
                  setOriginalPlantInfo({ ...updatedPlantInfo });
                } catch (parseError) {
                  setError('Failed to load plant data');
                }
              } else {
                setError('Failed to load plant data: No valid ID or stored data available');
              }
            }
          } else {
            // No valid GUID available, try localStorage
            const storedPlantData = localStorage.getItem('currentPlantData');
            if (storedPlantData) {
              try {
                const plant = JSON.parse(storedPlantData);
                setPlantData(plant);
                const updatedPlantInfo = {
                  customer: plant.customer,
                  site: plant.site,
                  plantName: plant.plant,
                  status: plant.status as 'Active' | 'Inactive',
                  latitude: plant.latitude || '',
                  longitude: plant.longitude || ''
                };
                setPlantInfo(updatedPlantInfo);
                setOriginalPlantInfo({ ...updatedPlantInfo });
              } catch (parseError) {
                setError('Failed to load plant data');
              }
            } else {
              setError(`Unable to load plant data: Invalid plant ID format (${plantId})`);
            }
          }
        }
      } catch (err) {
        setError('Failed to load data');
      } finally {
        setLoading(false);
      }
    };

    fetchInitialData();
  }, [plantId, dispatch]);



  useEffect(() => {
    if (plantId && plantData) {
      try {
        type PlantStatus = 'Active' | 'Inactive';
        const plantInfoData = {
          customer: plantData.customer || '',
          site: plantData.site || '',
          plantName: plantData.plant || '',
          status: (plantData.status === 'Active' ? 'Active' : 'Inactive') as PlantStatus,
          latitude: plantData.latitude || '',
          longitude: plantData.longitude || ''
        };
        
        // Update both Redux state and local state
        dispatch({ type: 'createPlant/setPlantInfo', payload: plantInfoData });
        setPlantInfo(plantInfoData);
        
        // Set original plant info for change detection
        setOriginalPlantInfo(plantInfoData);
        
        // Find and set the customer based on the plant data
        if (plantData.customerId) {
          const customer = customers.find(c => c.customerId === plantData.customerId);
          if (customer) {
            setSelectedCustomer(customer);
            // Load sites for the selected customer
            if (customer.sites && customer.sites.length > 0) {
              setSites(customer.sites);
            }
          } else {
            // If exact match fails, try to find by customer name
            const customerByName = customers.find(c => 
              c.customerName.includes(plantData.customer) || 
              plantData.customer.includes(c.customerName)
            );
            if (customerByName) {
              setSelectedCustomer(customerByName);
              if (customerByName.sites && customerByName.sites.length > 0) {
                setSites(customerByName.sites);
              } else {
                setSites([]);
              }
            }
          }
        }
      } catch (err) {
        setError('Failed to load plant data');
      }
    }
  }, [plantId, plantData, customers, dispatch]);

  useEffect(() => {
    if (selectedCustomer) {
      // Use sites directly from customer data instead of fetching from API
      if (selectedCustomer.sites && selectedCustomer.sites.length > 0) {
        // Update Redux store with sites from customer data
        dispatch({
          type: 'plant/fetchSites/fulfilled',
          payload: selectedCustomer.sites
        });
      } else {
        // If no sites in customer data, clear sites
        dispatch({
          type: 'plant/fetchSites/fulfilled',
          payload: []
        });
      }
    }
  }, [selectedCustomer, dispatch]);

  // Initialize form with Redux state (only in edit mode and if local state is not already set)
  useEffect(() => {
    if (plantInfoRedux && mode === 'edit' && !plantInfo.plantName) {
      setPlantInfo({
        ...plantInfoRedux,
        latitude: (plantInfoRedux as any).latitude || '',
        longitude: (plantInfoRedux as any).longitude || ''
      });
      if ((plantInfoRedux as any).customerId) {
        const customer = customers.find(c => c.customerId === (plantInfoRedux as any).customerId);
        if (customer) {
          setSelectedCustomer(customer);
        }
      }
    }
  }, [plantInfoRedux, customers, mode, plantInfo.plantName]);

  // Load saved form data from localStorage (only in create mode)
  useEffect(() => {
    // Only load saved form data when in create mode
    if (mode === 'create') {
      const savedFormData = localStorage.getItem('createPlantFormData');
      if (savedFormData) {
        try {
          const parsedData = JSON.parse(savedFormData);
          setPlantInfo(parsedData.plantInfo);
          if (parsedData.selectedCustomer) {
            setSelectedCustomer(parsedData.selectedCustomer);
            // Use embedded sites data from saved customer
            if (parsedData.selectedCustomer.sites && parsedData.selectedCustomer.sites.length > 0) {
              setSites(parsedData.selectedCustomer.sites);
            }
          }
        } catch (err) {
          // Error loading saved form data
        }
      }
    }
  }, [dispatch, mode]);

  // Clear localStorage when navigating away (except when going to CreatePlantDetails)
  useEffect(() => {
    return () => {
      const currentPath = window.location.pathname;
      if (!currentPath.includes('create-plant-details') && !currentPath.includes('create-plant')) {
        // Navigating completely away from plant creation/editing
        localStorage.removeItem('createPlantFormData');
        dispatch({ type: 'createPlant/resetPlantForm' });
      }
    };
  }, [dispatch]);

  useEffect(() => {
    if (location.state?.resetForm) {
      // Reset all form fields
      setPlantInfo({
        customer: '',
        site: '',
        plantName: '',
        status: 'Active',
        latitude: '',
        longitude: ''
      });
      setSelectedCustomer(null);

      // Remove any saved form data from localStorage
      localStorage.removeItem('createPlantFormData');

      // Reset Redux slice if used
      dispatch({ type: 'createPlant/resetPlantForm' });

      // Optionally, clear navigation state so reset doesn't re-trigger
      if (window.history.replaceState) {
        const { state, ...rest } = location;
        window.history.replaceState(
          { ...location.state, resetForm: undefined },
          document.title,
          location.pathname
        );
      }
    }
  }, [location, dispatch]);

  // Update navigation state for breadcrumb when plant name changes
  useEffect(() => {
    if (plantId && plantInfo.plantName) {
      const currentState = window.history.state || {};
      const newState = {
        ...currentState,
        plantName: plantInfo.plantName,
        breadcrumbTitle: plantInfo.plantName
      };
      window.history.replaceState(newState, '', window.location.pathname + window.location.search);
    }
  }, [plantId, plantInfo.plantName]);

  // Handle customer selection from searchable dropdown
  const handleCustomerSelect = (customer: CustomerLite) => {
    setSelectedCustomer(customer);
    const customerInfo = {
      ...plantInfo,
      customer: customer.customerId,
      customerId: customer.customerId || '',
      customerName: customer.customerName || '',
      site: '' // Reset site when customer changes
    };
    setPlantInfo(customerInfo);
    dispatch({ type: 'createPlant/setPlantInfo', payload: customerInfo });
    
    // Reset site dropdown when customer changes
    setIsSiteDropdownOpen(false);
    setSiteSearchText('');
    
    // Use embedded sites data instead of making API call
    if (customer.sites && customer.sites.length > 0) {
      setSites(customer.sites);
    } else {
      setSites([]);
    }
    
    setIsCustomerDropdownOpen(false);
    setCustomerSearchText('');
  };

  // Handle site selection from searchable dropdown
  const handleSiteSelect = (site: Site) => {
    const siteInfo = {
      ...plantInfo,
      site: site.site,
      siteId: site.siteId || '',
      siteName: site.site,
      latitude: site.latitude || plantInfo.latitude,
      longitude: site.longitude || plantInfo.longitude
    };
    setPlantInfo(siteInfo);
    dispatch({ type: 'createPlant/setPlantInfo', payload: siteInfo });
    
    setIsSiteDropdownOpen(false);
    setSiteSearchText('');
  };

  // Handle map location selection
  const handleMapLocationSelect = (lat: number, lng: number) => {
    const updatedInfo = {
      ...plantInfo,
      latitude: lat.toFixed(6),
      longitude: lng.toFixed(6)
    };
    setPlantInfo(updatedInfo);
    dispatch({ type: 'createPlant/setPlantInfo', payload: updatedInfo });
  };

  // Handle coordinates input change
  const handleCoordinatesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setCoordinatesDisplay(value);
    
    const coords = value.split(',').map(coord => coord.trim());
    if (coords.length === 2) {
      const lat = parseFloat(coords[0]);
      const lng = parseFloat(coords[1]);
      
      if (!isNaN(lat) && !isNaN(lng)) {
        const updatedInfo = {
          ...plantInfo,
          latitude: lat.toString(),
          longitude: lng.toString()
        };
        setPlantInfo(updatedInfo);
        dispatch({ type: 'createPlant/setPlantInfo', payload: updatedInfo });
      }
    } else if (value === '') {
      const updatedInfo = {
        ...plantInfo,
        latitude: '',
        longitude: ''
      };
      setPlantInfo(updatedInfo);
      dispatch({ type: 'createPlant/setPlantInfo', payload: updatedInfo });
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;

    switch (name) {
      case 'customer':
        // This will be handled by searchable dropdown now
        break;

      case 'site':
        // This will be handled by searchable dropdown now
        break;

      case 'plantName':
      case 'status':
      case 'latitude':
      case 'longitude':
        const updatedInfo = {
          ...plantInfo,
          [name]: value
        };
        setPlantInfo(updatedInfo);
        dispatch({ type: 'createPlant/setPlantInfo', payload: updatedInfo });
        break;

      default:
        // Unhandled input name
    }
  };  

  // Function to check if plant data has changed
  const hasPlantDataChanged = (): boolean => {
    if (!originalPlantInfo || mode !== 'edit') {
      return false;
    }
    
    const changes = {
      plantName: originalPlantInfo.plantName !== plantInfo.plantName,
      status: originalPlantInfo.status !== plantInfo.status,
      customer: originalPlantInfo.customer !== plantInfo.customer,
      site: originalPlantInfo.site !== plantInfo.site,
      latitude: originalPlantInfo.latitude !== plantInfo.latitude,
      longitude: originalPlantInfo.longitude !== plantInfo.longitude
    };
    
    const hasChanged = Object.values(changes).some(changed => changed);

    return hasChanged;
  };

  const handleSaveAndNext = async () => {
    //preventDefault();
              
    // Client-side validation
    const validationErrors: string[] = [];
    
    // For edit mode, if we have plant data but no selectedCustomer, it's likely a data mismatch
    // but we can still proceed if we have the basic plant info
    if (mode === 'create' && (!selectedCustomer || !plantInfo.customer)) {
      validationErrors.push('Customer is required');
    }
    
    if (mode === 'edit' && !plantInfo.customer) {
      validationErrors.push('Customer information is missing');
    }
    
    if (!plantInfo.site) {
      validationErrors.push('Site information is missing - plantInfo.site is empty');
    }
    
    // For edit mode, we don't need to validate that the site exists in the customer's sites
    // as the plant already exists with this configuration
    if (mode === 'create' && plantInfo.site && selectedCustomer && selectedCustomer.sites) {
      const siteExists = selectedCustomer.sites.some(s => s.site === plantInfo.site);
      if (!siteExists) {
        validationErrors.push(`Site '${plantInfo.site}' is not available for customer '${selectedCustomer.customerName}'`);
      }
    }
    
    if (!plantInfo.plantName || plantInfo.plantName.trim() === '') {
      validationErrors.push('Plant name is required');
    }
    
    if (validationErrors.length > 0) {
      toast.error(validationErrors.join(', '));
      return;
    }

    try {
      setLoading(true);


      // Save form data to localStorage
      const formDataToSave = {
        plantInfo,
        selectedCustomer
      };
      localStorage.setItem('createPlantFormData', JSON.stringify(formDataToSave));
      const selectedSite = sites.find((s) => s.site.trim().toLowerCase() === plantInfo.site.trim().toLowerCase());

      // Skip site validation and proceed with the entered site name
      const plantDataToStore = {
        customerId: selectedCustomer?.customerId || '',
        customerName: selectedCustomer?.customerName || '',
        siteId: selectedSite?.siteId || '',
        siteName: selectedSite?.site || '',
        plantName: plantInfo.plantName,
        plantStatus: plantInfo.status,
        assets: plantData?.assets || []
      };
     
      // Handle edit mode
      if (plantId) {
        // Ensure customerId is persisted for downstream pages (asset hierarchy, notifications)
        if (selectedCustomer?.customerId) {
          localStorage.setItem('customerid', selectedCustomer.customerId);
        }

        // Check if changes were made before calling API
        if (!hasPlantDataChanged()) {
          navigate('/create-plant-details');
          return;
        }

        //Find the selected customer to get customer details
        if (!selectedCustomer) {
          throw new Error('Please select a customer');
        }

        // Find the selected site to get site details
        const selectedSite = sites.find(site => site.site === plantInfo.site);
        if (!selectedSite) {
          throw new Error('Please select a valid site');
        }

        // Prepare payload for editPlantThunk with all required fields
        const editPayload = {
          id: selectedPlantId || plantData?.plantId || '',
          customerName: selectedCustomer.customerName,
          siteName: selectedSite.site,
          plantName: plantInfo.plantName,
          plantStatus: plantInfo.status,
          cnxSiteId: parseInt(selectedSite.siteId),
          latitude: plantInfo.latitude || '0',
          longitude: plantInfo.longitude || '0'
        };
        
        const result = await dispatch(editPlantThunk(editPayload));
        
        if (editPlantThunk.fulfilled.match(result)) {
          // Update original plant info after successful save
          setOriginalPlantInfo({ ...plantInfo });
          toast.success('Plant updated successfully');
          setTimeout(() => navigate('/create-plant-details'), 1200);
          return;
        } else {
          // Handle specific "Plant already exists" error
          const errorData = result.payload as any;
          if (errorData?.data?.Message?.includes('already exists')) {
            // This is a known backend issue - treat as success for now
            setOriginalPlantInfo({ ...plantInfo });
            toast.success('Plant coordinates updated successfully');
            setTimeout(() => navigate('/create-plant-details'), 1200);
            return;
          } else {
            throw new Error(errorData?.data?.Message || 'Failed to update plant');
          }
        }
      }
      // Handle create mode
        const newPlantData = {
          customerName: selectedCustomer?.customerName || '',
          siteName: selectedSite?.site || '',
          plantName: plantInfo.plantName,
          plantStatus: plantInfo.status,
          cnxSiteId: parseInt(selectedSite?.siteId || '0'),
          latitude: plantInfo.latitude || '0',
          longitude: plantInfo.longitude || '0'
        };

        const response = await createPlant(newPlantData);
        
        if (response.Success && response.Data) {
          // Update plantDataToStore with the returned GUID
          const updatedPlantDataToStore = {
            ...plantDataToStore,
            plantId: response.Data, // Add the GUID returned from API
            id: response.Data // Also set id for consistency
          };
          
          localStorage.setItem('currentplantguid', response.Data);
          localStorage.setItem('currentPlantId', response.Data);
          localStorage.setItem('currentPlantData', JSON.stringify(updatedPlantDataToStore));
          localStorage.setItem('customerid', plantDataToStore.customerId || '');
          // Trigger plant list refresh
          if (window.refreshPlantList) {
            window.refreshPlantList();
          }

          toast.success('Plant Saved Successfully');
          setTimeout(() => navigate('/create-plant-details'), 1200);
        } else {
          // Handle API response with error message
          const errorMessage = response.Message || 'Failed to create plant';
          
          setError(errorMessage);
          toast.error(errorMessage);
          return;
        }
    } catch (err: any) {
      // Extract error message from API response
      let errorMessage = 'Failed to save plant. Please try again.';
      
      // Handle specific API error responses
      if (err.response?.data?.Message) {
        // Show the exact API error message
        errorMessage = err.response.data.Message;
      } else if (err.response?.data?.errors) {
        // Handle validation errors from API
        const errors = err.response.data.errors;
        const errorMessages = [];
        
        for (const [field, messages] of Object.entries(errors)) {
          if (Array.isArray(messages)) {
            errorMessages.push(`${field}: ${messages.join(', ')}`);
          }
        }
        
        if (errorMessages.length > 0) {
          errorMessage = `Validation errors: ${errorMessages.join('; ')}`;
        }
      } else if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Step 3: Add handler for Next in edit mode
  const handleNext = async () => {
    // Check if we're in edit mode and if any changes were made
    const hasChanges = hasPlantDataChanged();

    // Ensure customerId is persisted for downstream pages (asset hierarchy, notifications)
    if (selectedCustomer?.customerId) {
      localStorage.setItem('customerid', selectedCustomer.customerId);
    }

    if (mode === 'edit' && !hasChanges) {
      // No changes detected, navigate to next page without API call
      // Use ObjectId format for CreatePlantDetails
      navigate('/create-plant-details', { state: { plantId: plantData?.id } });
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      // Find the selected customer to get customer details
      if (!selectedCustomer) {
        throw new Error('Please select a customer');
      }

      // Find the selected site to get site details
      const selectedSite = sites.find(site => site.site === plantInfo.site);
      if (!selectedSite) {
        throw new Error('Please select a valid site');
      }
      
      // Prepare payload for editPlantThunk with all required fields
      const CreatePlantEditPayload = {
        id: selectedPlantId || plantData?.plantId || '', // Use plantId (GUID) instead of id (ObjectId)
        customerName: selectedCustomer.customerName,
        siteName: selectedSite.site,
        plantName: plantInfo.plantName,
        plantStatus: plantInfo.status,
        cnxSiteId: parseInt(selectedSite.siteId),
        latitude: plantInfo.latitude || '0',
        longitude: plantInfo.longitude || '0'
      };
      const result = await dispatch(editPlantThunk(CreatePlantEditPayload));
      
      if (editPlantThunk.fulfilled.match(result)) {
        // Update original plant info after successful save
        setOriginalPlantInfo({ ...plantInfo });
        toast.success('Plant Saved Successfully');
        // Use ObjectId format for CreatePlantDetails, not the GUID from editPayload.id
        setTimeout(() => navigate('/create-plant-details', { state: { plantId: plantData?.id } }), 1200);
      } else if (editPlantThunk.rejected.match(result)) {
        // Handle error from edit plant thunk
        let errorMessage = 'Failed to update plant. Please try again.';
        
        if (result.payload && typeof result.payload === 'object') {
          const error = result.payload as any;
          
          // Check if it's the validation error format with errors object
          if (error.response?.data?.errors && typeof error.response.data.errors === 'object') {
            const errors = error.response.data.errors;
            const errorMessages: string[] = [];
            
            // Extract all error messages from the errors object
            Object.keys(errors).forEach(field => {
              if (Array.isArray(errors[field])) {
                errors[field].forEach((msg: string) => {
                  errorMessages.push(msg);
                });
              }
            });
            
            if (errorMessages.length > 0) {
              errorMessage = errorMessages.join(', ');
            }
          }
          // Check other error formats
          else if (error.response?.data?.title) {
            errorMessage = error.response.data.title;
          } else if (error.message) {
            errorMessage = error.message;
          }
        }
        
        toast.error(errorMessage);
      }
    } catch (err: any) {
      let errorMessage = 'Failed to update plant. Please try again.';
      
      if (err.response?.data) {
        // First check for direct API message
        if (err.response.data.Message) {
          errorMessage = err.response.data.Message;
        } else if (err.response.data.message) {
          errorMessage = err.response.data.message;
        }
        // Check if it's the validation error format with errors object
        else if (err.response.data.errors && typeof err.response.data.errors === 'object') {
          const errors = err.response.data.errors;
          const errorMessages: string[] = [];
          
          // Extract all error messages from the errors object
          Object.keys(errors).forEach(field => {
            if (Array.isArray(errors[field])) {
              errors[field].forEach((msg: string) => {
                errorMessages.push(msg);
              });
            }
          });
          
          if (errorMessages.length > 0) {
            errorMessage = errorMessages.join(', ');
          }
        }
        // Check other error formats
        else if (err.response.data.title) {
          errorMessage = err.response.data.title;
        } else if (err.message) {
          errorMessage = err.message;
        }
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleDeletePlant = () => {
    if (!plantId || !plantData) {
      toast.error('No plant data available to delete');
      return;
    }
    setShowDeleteModal(true);
  };

  const confirmDeletePlant = async () => {
    if (!plantId) return;

    try {
      setDeleting(true);
      setError(null);

      const response = await deletePlantById(plantId);
      
      if (response.data && response.data.Success) {
        setShowDeleteModal(false);
        window.scrollTo(0, 0);
        navigate('/plants', {
          state: { plantDeleted: true, message: 'Plant deleted successfully!' },
        });
      } else {
        const errorMessage = response.data?.Message || 'Failed to delete plant';
        setError(errorMessage);
        toast.error(`Error: ${errorMessage}`);
      }
    } catch (err) {
      const errorMessage = 'Failed to delete plant. Please try again.';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setDeleting(false);
      setShowDeleteModal(false);
    }
  };

  const cancelDeletePlant = () => {
    setShowDeleteModal(false);
  };

  const handleCancel = () => {
    // Clear saved form data from localStorage
    
    
    // Reset Redux state if needed
    dispatch({ type: 'createPlant/resetPlantForm' });
    
     window.scrollTo(0, 0);
    // Navigate back to plants list
    navigate('/plants');
  };

  // Tab navigation handler
  const handleTabNavigation = (tabNumber: number) => {
    const plantGuid = localStorage.getItem('currentplantguid');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    switch (tabNumber) {
      case 1:
        // Always allow navigation to step 1 (current page)
        break;
      case 2:
        // Navigate to Create Plant Details
        if (plantGuid || (mode === 'edit' && plantId)) {
          navigate('/create-plant-details');
        } else {
          // Show error or prompt to save plant first
          setError('Please save the plant details first before proceeding to asset hierarchy.');
        }
        break;
      case 3:
        // Navigate to Notifications
        if (plantGuid || (mode === 'edit' && plantId)) {
          navigate('/create-plant-details/notification');
        } else {
          setError('Please save the plant details first before proceeding to notification setup.');
        }
        break;
      default:
        break;
    }
  };

  return (<>
  {loading && <Loader isOverlay={true} />}
  <ConfirmationModal
    isOpen={showDeleteModal}
    title="Delete Plant"
    message={`Are you sure you want to delete the plant "${plantData?.plant ?? ''}"? This action cannot be undone.`}
    confirmText="Delete"
    cancelText="Cancel"
    onConfirm={confirmDeletePlant}
    onCancel={cancelDeletePlant}
    isDestructive={true}
  />
    <div className="create-plant-container" style={{ position: 'relative' }}>
      
      <div className="create-plant-header">
        {/* <div className="breadcrumb">
          <span>Home</span> \ <span>Account management</span> \ <span>{plantId ? 'Edit plant' : 'Create plant'}</span>
        </div> */}
      </div>

      <div className="create-plant-tabs">
        <div 
          className={`tab ${currentStep === 1 ? 'active' : ''} ${mode === 'edit' ? 'clickable' : ''}`}
          onClick={mode === 'edit' ? () => handleTabNavigation(1) : undefined}
        >
          <span className="tab-number">1</span>
          {plantId ? 'Edit Plant Details' : 'Enter Plant Details'}
        </div>
        <div 
          className={`tab ${currentStep === 2 ? 'active' : ''} ${mode === 'edit' ? 'clickable' : ''}`}
          onClick={mode === 'edit' ? () => handleTabNavigation(2) : undefined}
        >
          <span className="tab-number">2</span>
          Build Asset Hierarchy
        </div>
            <div 
                  className={`tab ${currentStep === 3 ? 'active' : ''} ${
                      mode === 'edit' && assets.length > 0 ? 'clickable' : 'disabled'
                  }`}
                  onClick={mode === 'edit' && assets.length > 0 ? () => handleTabNavigation(3) : undefined}
              >
                  <span className="tab-number">3</span>
                  Notification Setup
              </div>
      </div>      

      <div className="plant-information-section">
        <h3>Plant Information</h3>
        <div className="content-layout">
          <div className="form-container">              
            <div className="form-group">
                <label>Customer<span className='required'>*</span></label>
                {loading ? (
                  <div>Loading...</div>
                ) : plantId ? (
                  <input
                    type="text"
                    className="form-control"
                    value={selectedCustomer?.customerName || plantInfo.customer || ''}
                    disabled
                    style={{width: '100%'}}
                  />
                ) : (
                  <div className="searchable-select-container" style={{ position: 'relative', width: '100%' }}>
                    <div className="searchable-select-input" onClick={() => { 
                      const next = !isCustomerDropdownOpen; 
                      setIsCustomerDropdownOpen(next); 
                      setIsSiteDropdownOpen(false);
                      if (!next) setCustomerSearchText('');
                    }}>
                      <input
                        type="text"
                        value={isCustomerDropdownOpen ? customerSearchText : (selectedCustomer ? `${selectedCustomer.customerName}${selectedCustomer.soldTo ? ` (#${selectedCustomer.soldTo})` : ''}` : '')}
                        onChange={(e) => setCustomerSearchText(e.target.value)}
                        onFocus={() => { /* no-op to avoid first-click open/close race */ }}
                        onKeyDown={(e) => {
                          if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
                            setIsCustomerDropdownOpen(true);
                          }
                        }}
                        placeholder={isCustomerDropdownOpen ? 'Search customers...' : 'Select Customer'}
                        className="form-control searchable-input"
                        readOnly={!isCustomerDropdownOpen}
                        disabled={loading}
                        style={{ 
                          paddingRight: '44px', 
                          cursor: isCustomerDropdownOpen ? 'text' : 'pointer',
                          borderBottomLeftRadius: isCustomerDropdownOpen ? '0' : undefined,
                          borderBottomRightRadius: isCustomerDropdownOpen ? '0' : undefined,
                          borderBottom: isCustomerDropdownOpen ? 'none' : undefined,
                          marginBottom: isCustomerDropdownOpen ? '0' : undefined,
                          zIndex: isCustomerDropdownOpen ? 1001 : undefined,
                          width: '100%'
                        }}
                      />
                      <span className={`dropdown-arrow ${isCustomerDropdownOpen ? 'open' : ''}`} style={{ 
                        position: 'absolute', 
                        right: '14px', 
                        top: '50%', 
                        transform: 'translateY(-50%)', 
                        color: isCustomerDropdownOpen ? '#60bfda' : '#94a3b8',
                        fontSize: '14px',
                        pointerEvents: 'none'
                      }}>
                        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M4.16797 7.5L10.0013 13.3333L15.8346 7.5" stroke="#FCFCFD" stroke-width="1.66667" stroke-linecap="round" stroke-linejoin="round"/>
                           </svg>
                      </span>
                    </div>
                    {isCustomerDropdownOpen && (
                      <div className="searchable-dropdown" style={{
                        position: 'absolute',
                        top: 'calc(100% - 1px)',
                        left: 0,
                        marginTop: '0',
                        backgroundColor: '#0f172a',
                        border: '1px solid #1e293b',
                        borderTop: '1px solid #1e293b',
                        borderRadius: '0 0 10px 10px',
                        padding: '8px 0',
                        width: '100%',
                        maxHeight: '300px',
                        overflowY: 'auto',
                        zIndex: 1000,
                        boxShadow: '0 18px 36px -14px rgba(15, 23, 42, 0.85)'
                      }}>
                        {filteredCustomers.map((customer, index) => {
                          const label = `${customer.customerName}${customer.soldTo ? ` (#${customer.soldTo})` : ''}`;
                          return (
                            <div
                              key={customer.id || `${customer.customerId}-${index}`}
                              className={`dropdown-option ${selectedCustomer?.customerId === customer.customerId ? 'selected1' : ''}`}
                              onClick={() => handleCustomerSelect(customer)}
                              style={{
                                padding: '10px 18px',
                                color: '#f8fafc',
                                cursor: 'pointer',
                                fontSize: '14px',
                                backgroundColor: selectedCustomer?.customerId === customer.customerId ? 'rgba(96, 191, 218, 0.16)' : 'transparent',
                                transition: 'background-color 0.15s ease'
                              }}
                              onMouseEnter={(e) => {
                                if (selectedCustomer?.customerId !== customer.customerId) {
                                  e.currentTarget.style.backgroundColor = 'rgba(59, 130, 246, 0.12)';
                                }
                              }}
                              onMouseLeave={(e) => {
                                if (selectedCustomer?.customerId !== customer.customerId) {
                                  e.currentTarget.style.backgroundColor = 'transparent';
                                }
                              }}
                            >
                              {label}
                            </div>
                          );
                        })}
                        {filteredCustomers.length === 0 && (
                          <div style={{ padding: '10px 18px', color: '#94a3b8', fontSize: '14px' }}>
                            No customers found
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
            </div>

            <div className="form-group">
              <label>Site<span className='required'>*</span></label>
              {plantId ? (
                // In edit mode, show a disabled input with the site name
                <input
                  type="text"
                  className="form-control"
                  value={plantInfo.site}
                  disabled
                  style={{width: '100%'}}
                />
              ) : (
                // In create mode, show the searchable dropdown
                <div className="searchable-select-container" style={{ position: 'relative', width: '100%' }}>
                  <div className="searchable-select-input" onClick={() => { 
                    if (selectedCustomer) {
                      const next = !isSiteDropdownOpen; 
                      setIsSiteDropdownOpen(next); 
                      setIsCustomerDropdownOpen(false);
                      if (!next) setSiteSearchText('');
                    }
                  }}>
                    <input
                      type="text"
                      value={isSiteDropdownOpen ? siteSearchText : (plantInfo.site || '')}
                      onChange={(e) => setSiteSearchText(e.target.value)}
                      onFocus={() => { /* no-op to avoid first-click open/close race */ }}
                      onKeyDown={(e) => {
                        if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
                          setIsSiteDropdownOpen(true);
                        }
                      }}
                      placeholder={isSiteDropdownOpen ? 'Search sites...' : 'Select Site'}
                      className="form-control searchable-input"
                      readOnly={!isSiteDropdownOpen}
                      disabled={loading || !selectedCustomer}
                      style={{ 
                        paddingRight: '44px', 
                        cursor: isSiteDropdownOpen ? 'text' : 'pointer',
                        borderBottomLeftRadius: isSiteDropdownOpen ? '0' : undefined,
                        borderBottomRightRadius: isSiteDropdownOpen ? '0' : undefined,
                        borderBottom: isSiteDropdownOpen ? 'none' : undefined,
                        marginBottom: isSiteDropdownOpen ? '0' : undefined,
                        zIndex: isSiteDropdownOpen ? 1001 : undefined,
                         width: '100%'
                      }}
                    />
                    <span className={`dropdown-arrow ${isSiteDropdownOpen ? 'open' : ''}`} style={{ 
                      position: 'absolute', 
                      right: '14px', 
                      top: '50%', 
                      transform: 'translateY(-50%)', 
                      color: isSiteDropdownOpen ? '#60bfda' : '#94a3b8',
                      fontSize: '14px',
                      pointerEvents: 'none'
                    }}>
                      <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M4.16797 7.5L10.0013 13.3333L15.8346 7.5" stroke="#FCFCFD" stroke-width="1.66667" stroke-linecap="round" stroke-linejoin="round"/>
                           </svg>
                    </span>
                  </div>
                  {isSiteDropdownOpen && selectedCustomer && (
                    <div className="searchable-dropdown" style={{
                      position: 'absolute',
                      top: 'calc(100% - 1px)',
                      left: 0,
                      marginTop: '0',
                      backgroundColor: '#0f172a',
                      border: '1px solid #1e293b',
                      borderTop: '1px solid #1e293b',
                      borderRadius: '0 0 10px 10px',
                      padding: '8px 0',
                      width: '100%',
                      maxHeight: '300px',
                      overflowY: 'auto',
                      zIndex: 1000,
                      boxShadow: '0 18px 36px -14px rgba(15, 23, 42, 0.85)'
                    }}>
                      {filteredSites.map((site, index) => (
                        <div
                          key={`${site.siteId}-${index}`}
                          className={`dropdown-option ${plantInfo.site === site.site ? 'selected1' : ''}`}
                          onClick={() => handleSiteSelect(site)}
                          style={{
                            padding: '10px 18px',
                            color: '#f8fafc',
                            cursor: 'pointer',
                            fontSize: '14px',
                            backgroundColor: plantInfo.site === site.site ? 'rgba(96, 191, 218, 0.16)' : 'transparent',
                            transition: 'background-color 0.15s ease'
                          }}
                          onMouseEnter={(e) => {
                            if (plantInfo.site !== site.site) {
                              e.currentTarget.style.backgroundColor = 'rgba(59, 130, 246, 0.12)';
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (plantInfo.site !== site.site) {
                              e.currentTarget.style.backgroundColor = 'transparent';
                            }
                          }}
                        >
                          {site.site}
                        </div>
                      ))}
                      {filteredSites.length === 0 && (
                        <div style={{ padding: '10px 18px', color: '#94a3b8', fontSize: '14px' }}>
                          No sites found
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="form-group">
              <label>Plant Name<span className='required'>*</span></label>
              <input
                type="text"
                name="plantName"
                value={plantInfo.plantName}
                onChange={handleInputChange}
                placeholder="Enter plant name"
                disabled={loading}
              />
            </div>

            <div className="form-group">
              <label>Status</label>
              <select
                name="status"
                value={plantInfo.status}
                onChange={handleInputChange}
                disabled={loading}
              >
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
              </select>
            </div>
             <div className="form-group" style={{ marginBottom: '16px' }}>
                <label>Coordinates</label>
                <input
                  type="text"
                  value={coordinatesDisplay}
                  onChange={handleCoordinatesChange}
                  placeholder="Enter Latitude, Longitude (e.g., 40.7128, -74.0060)"
                  disabled={loading}

                  style={{ 
                    width: '100%', 
                    padding: '8px 12px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '14px'
                  }}
                />
              </div>

            <div className="button-container" style={{ position: 'relative', zIndex: 1000 }}>
              <button
                type="button"
                className="cancel-button-plant"
                onClick={handleCancel}
                disabled={loading}
              >
                Cancel
              </button>
              {plantId && (
                <button
                  className="delete-button"
                  onClick={handleDeletePlant}
                  disabled={loading || deleting}
                >
                  {deleting ? 'Deleting...' : 'Delete Plant'}
                </button>
              )}
              {/* Step 2: Conditionally render button based on mode */}
              {mode === 'create' ? (
                <button
                  type="button"
                  className="next-button"
                  onClick={handleSaveAndNext}
                  disabled={loading}
                  style={{ 
                    cursor: 'pointer', 
                    pointerEvents: 'auto',
                    position: 'relative',
                    zIndex: 1001,
                    backgroundColor: '#0094BD',
                    border: 'none',
                    color: 'white',
                    width: 'auto',
                    minWidth: 'fit-content',
                    whiteSpace: 'nowrap',
                    padding: '8px 16px'
                  }}
                >
                  {loading ? 'Saving...' : 'Save & Next'}
                </button>
              ) : (
                <button
                  type="button"
                  className="next-button"
                  onClick={handleNext}
                  disabled={loading}
                  style={{ 
                    cursor: 'pointer', 
                    pointerEvents: 'auto',
                    position: 'relative',
                    zIndex: 1001,
                    backgroundColor: '#0094BD',
                    //opacity: hasPlantDataChanged() ? 1 : 0.8
                  }}
                >
                  {loading ? 'Next' : hasPlantDataChanged() ? 'Save & Next' : 'Next'}
                </button>
              )}
            </div>
          </div>
          
          {/* Latitude and Longitude Section */}
          <div className="map-container">
            <div className="site-location-container">
              <h4>Plant Location</h4>
              <div className="map-wrapper">
                <MapSelector
                  latitude={plantInfo.latitude}
                  longitude={plantInfo.longitude}
                  onLocationSelect={handleMapLocationSelect}
                  height="280px"
                  disabled={loading}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      <ToastContainer 
        className="toastify-message-plant" 
        position="top-right" 
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        style={{ zIndex: 9999 }}
      />
    </div>
  </>
  );
};

export default CreatePlant; 	